import { Container, Rectangle, Sprite, Text, type Texture } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';

/** Default alias the host themes register the coin icon under
 * (asset brief §7 — `icon/coin.png`). Mirrors
 * {@link ../BalanceDisplay BalanceDisplay#DEFAULT_COIN_ICON_ALIAS} so
 * both widgets pick up the same sprite without per-instance plumbing. */
const COIN_ICON_ALIAS = 'shared:icon:coin';
/** Locked-coin variant — same coin sprite with a painted padlock
 *  inside. Used when the bet pill is interactive-disabled (multiplayer
 *  Reel Race / onboarding session 1) so the player gets a visual
 *  cue that the value isn't tappable. Asset commissioned 2026-05-12;
 *  registered as a sibling of {@link COIN_ICON_ALIAS} via
 *  {@code SHARED_UI_ICONS} in {@code apps/mobile/src/themes/index.ts}. */
const COIN_LOCKED_ICON_ALIAS = 'shared:icon:coin-locked';
/** Pill height — slightly chunkier than the previous 56 so the navy +
 *  gold pill reads with the same visual weight as the new larger SPIN
 *  disc. Producer audit Phase 4 — "all-in-one cohesive pill" spec from
 *  the slot-HUD redesign brief. */
const PILL_HEIGHT = 64;
/** Inner horizontal padding inside the pill. The pill auto-sizes around
 *  the digits + coin sprite so 1-digit and 6-digit bets both feel
 *  composed instead of either cramped or floating in dead space. */
const PILL_PADDING_X = 22;
/** Minimum pill width — tight enough that small bets ("5", "10")
 *  visibly shrink the pill against larger ones ("100", "250").
 *  Producer brief 2026-05-12: "the hud size should adjust based on
 *  the width of the bet number. At the moment it's fixed." Was 130
 *  before — wide enough that 1- and 3-digit bets settled on the
 *  same width. Dropping to 100 lets the auto-sizer in {@link redraw}
 *  do real work for low-digit-count bets. */
const PILL_MIN_WIDTH = 100;
/** Gap between the digits and the coin sprite. */
const COIN_DIGIT_GAP = 8;
/** Coin sprite display height, in widget-local units. Producer
 *  brief 2026-05-12: "the coin size in both should be somewhere in
 *  the middle." Previously 36 (single-player) vs 56 (locked) —
 *  unified at 46 so both modes read with the same coin presence,
 *  splitting the difference between the prior extremes. The locked
 *  variant still shifts right inside the pill ({@link
 *  COIN_LOCKED_RIGHT_TIGHTEN}) to absorb the padlock PNG's
 *  transparent margin. */
const COIN_SPRITE_HEIGHT = 46;
/** How many widget-local units to tighten the pill (and shift the
 *  content block right) when the locked coin is showing, to absorb
 *  the coin-locked PNG's transparent margin. Slightly smaller now
 *  that the locked coin matches the regular coin height (46 → less
 *  absolute margin to compensate for). */
const COIN_LOCKED_RIGHT_TIGHTEN = 8;
/** Locked-state coin height — matches {@link COIN_SPRITE_HEIGHT}
 *  per producer brief 2026-05-12 ("somewhere in the middle"). Kept
 *  as a separate constant so the two modes can diverge again
 *  without re-threading the size through every call site. */
const COIN_LOCKED_SPRITE_HEIGHT = 46;
/** Digit font size. Bigger than the legacy 22 so the bet amount reads
 *  as the visual focal point of the pill (per the design concept,
 *  where the digits are stamped-gold and dominate the capsule). */
const DIGIT_FONT_SIZE = 30;

// Pill chrome (navy gradient + gold border) now lives on the host
// {@link ../../scenes/components/SlotHudComponent SlotHudComponent}'s
// betPillPlinth Graphics so it draws at world scale, matching the
// sibling INFO + SETTINGS cluster pill pixel-for-pixel on every
// viewport. The palette constants moved there too — see that file
// for the matching {0x16113d, 0x2a1e63, 0xffc940, 0xffe28a} stack.
/** Digit ink — warm gold matching the border. */
const DIGIT_COLOR = 0xffe28a;
/** Digit drop-shadow ink — pure black at low alpha so the digits stamp
 *  cleanly against the navy. Pixi's Text doesn't expose a drop-shadow
 *  in the type defs reliably across versions, so we use a darker
 *  underlay text plus the main text on top in {@link redraw}. */
const DIGIT_SHADOW_COLOR = 0x000000;

/**
 * Compact bet-amount selector. Tap to cycle to the next amount in the
 * configured ladder; long-tap or right-click cycles backwards. The host
 * scene listens via {@link onBetChange} and uses {@link currentBet} when
 * driving a spin.
 *
 * <p>Producer audit Phase 4 (slot-HUD redesign brief) — the widget is
 * now a single cohesive pill: no visible +/- sub-buttons, no "BET"
 * caption, no separate background sprite. The whole capsule is the
 * tap target, the coin glyph sits next to the gold digits, and the
 * pill auto-sizes around the bet's digit count (3, 4, 5, 6 digits all
 * fit without cramping). The painted +/- commissioned assets are
 * intentionally unused — the design brief retired them in favour of
 * tap-to-cycle.
 */
export class BetSelector extends Container {
  private readonly digitText: Text;
  private readonly digitShadow: Text;
  private readonly coinSprite: Sprite | null;
  private currentIndex: number;
  private interactive_ = true;
  /** Last computed pill width — exposed via {@link pillWidth} so the
   *  parent {@link ../../scenes/components/SlotHudComponent SlotHudComponent}
   *  can lay out around the auto-sized capsule without guessing. The
   *  parent uses this width to paint the pill background (gradient +
   *  gold border) at world scale, so its chrome matches the sibling
   *  cluster pill pixel-for-pixel. */
  private renderedWidth = PILL_MIN_WIDTH;
  /** Pill height in widget-local units. Exposed so the host can size
   *  its world-scale background plinth to match. */
  static readonly PILL_HEIGHT = PILL_HEIGHT;

  public onBetChange: ((bet: number) => void) | null = null;
  /** Fires when the player taps the selector while it's locked
   *  (non-interactive). The host surfaces a "Locked for multiplayer"
   *  explainer so the tap isn't a dead no-op — the player learns WHY
   *  they can't change the bet. Null = no explainer (the tap silently
   *  no-ops, the legacy behaviour). */
  public onLockedTap: (() => void) | null = null;

  constructor(public readonly amounts: readonly number[], initialBet?: number) {
    super();
    if (amounts.length === 0) {
      throw new Error('BetSelector: at least one bet amount required');
    }
    this.currentIndex = Math.max(
      0,
      amounts.findIndex((a) => a === initialBet),
    );

    // Drop-shadow underlay for the digits — same string, dark ink,
    // sits 2px below the main label. Cheaper than a real shadow filter
    // and renders cleanly across Pixi versions.
    this.digitShadow = new Text({
      text: '',
      style: {
        fontFamily: '"Fredoka", system-ui, -apple-system, sans-serif',
        fontSize: DIGIT_FONT_SIZE,
        fontWeight: '900',
        fill: DIGIT_SHADOW_COLOR,
      },
    });
    this.digitShadow.anchor.set(0.5, 0.5);
    this.digitShadow.alpha = 0.55;
    this.addChild(this.digitShadow);

    this.digitText = new Text({
      text: '',
      style: {
        fontFamily: '"Fredoka", system-ui, -apple-system, sans-serif',
        fontSize: DIGIT_FONT_SIZE,
        fontWeight: '900',
        fill: DIGIT_COLOR,
        // Letter-spacing helps the gold digits read as "stamped"
        // rather than condensed; matches the SPIN button's typo.
        letterSpacing: 1,
      },
    });
    this.digitText.anchor.set(0.5, 0.5);
    this.addChild(this.digitText);

    // Coin sprite — to the right of the digits. When the texture isn't
    // registered (engine-only test scenes) we render digits-only and
    // shrink the pill accordingly via the auto-sizing in {@link redraw}.
    const coinTexture = AssetLoader.shared.getTexture(COIN_ICON_ALIAS);
    if (coinTexture) {
      applySmoothMinification(coinTexture);
      this.coinSprite = new Sprite(coinTexture);
      this.coinSprite.anchor.set(0.5, 0.5);
      const aspect = coinTexture.width / coinTexture.height;
      this.coinSprite.height = COIN_SPRITE_HEIGHT;
      this.coinSprite.width = COIN_SPRITE_HEIGHT * aspect;
      this.addChild(this.coinSprite);
    } else {
      this.coinSprite = null;
    }

    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointertap', () => {
      // Locked selector: don't cycle, but DO tell the host so it can
      // explain why. Without this the tap is a dead no-op and the
      // player can't tell a locked bet from a broken one.
      if (!this.interactive_) {
        this.onLockedTap?.();
        return;
      }
      this.cycle(1);
    });
    this.on('rightclick', (event) => {
      event.preventDefault?.();
      this.cycle(-1);
    });

    this.redraw();
  }

  /** Currently selected bet amount. */
  get currentBet(): number {
    return this.amounts[this.currentIndex]!;
  }

  /** Rendered pill width in CSS pixels — used by the host to position
   *  sibling controls around the auto-sized capsule. Stable until the
   *  bet changes (which triggers {@link redraw} and a new measurement). */
  get pillWidth(): number {
    return this.renderedWidth;
  }

  /** Step to the next/previous bet, wrapping. Calls {@link onBetChange} on change. */
  cycle(direction: 1 | -1): void {
    if (!this.interactive_ || this.amounts.length <= 1) return;
    const len = this.amounts.length;
    this.currentIndex = (this.currentIndex + direction + len) % len;
    this.redraw();
    this.onBetChange?.(this.currentBet);
  }

  /** Show `amount` (one of the amounts) even while the pill is locked, so
   *  the pill always matches the model's bet. */
  select(amount: number): void {
    const index = this.amounts.indexOf(amount);
    if (index < 0 || index === this.currentIndex) return;
    this.currentIndex = index;
    this.redraw();
  }

  setInteractive(value: boolean): void {
    this.interactive_ = value;
    this.cursor = value ? 'pointer' : 'default';
    // Keep the pill fully opaque even when disabled — the painted
    // gold border + digits should never look washed out (that read as
    // broken in earlier producer audits). The cursor change is the
    // honest affordance signal.
    this.alpha = 1;
  }

  /** Swap the coin sprite to the locked-coin variant so the player
   *  reads "bet is locked" at a glance. Used by the multiplayer Reel
   *  Race (every spin runs at a fixed server-side notional bet) and
   *  onboarding session 1. Falls back to the regular coin texture
   *  when {@code visible} is false, OR when the locked variant
   *  isn't registered (engine-only test scenes that don't ship the
   *  commissioned icons).
   *
   *  Pixi v8 textures default to a {@code scaleMode} that doesn't
   *  filter cleanly when minifying ~1250px source down to ~36px
   *  display (the coin sprite height). Force {@code linear} +
   *  mipmaps on whichever coin texture is in use so the gold edge
   *  reads smooth instead of jaggy at small display sizes. */
  setLockBadgeVisible(visible: boolean): void {
    if (!this.coinSprite) return;
    const alias = visible ? COIN_LOCKED_ICON_ALIAS : COIN_ICON_ALIAS;
    const texture = AssetLoader.shared.getTexture(alias);
    if (!texture) return;
    applySmoothMinification(texture);
    this.coinSprite.texture = texture;
    const aspect = texture.width / texture.height;
    const height = visible ? COIN_LOCKED_SPRITE_HEIGHT : COIN_SPRITE_HEIGHT;
    this.coinSprite.height = height;
    this.coinSprite.width = height * aspect;
    // The pill width depends on the coin's rendered width via
    // {@link redraw}; re-running it keeps the digit + coin centred
    // and refreshes the host's pillWidth measurement.
    this.redraw();
  }


  /** Disable amounts above {@code maxAffordable} — the host clamps when the
   * player can't actually afford the next-up bet. Doesn't change the current
   * selection unless it's now unaffordable. */
  setMaxAffordable(maxAffordable: number): void {
    if (this.currentBet > maxAffordable) {
      // Snap down to the highest affordable amount.
      let bestIndex = -1;
      for (let i = 0; i < this.amounts.length; i++) {
        if (this.amounts[i]! <= maxAffordable) bestIndex = i;
      }
      if (bestIndex >= 0 && bestIndex !== this.currentIndex) {
        this.currentIndex = bestIndex;
        this.redraw();
        this.onBetChange?.(this.currentBet);
      }
    }
  }

  private redraw(): void {
    // 1) Set the text first so we can measure its rendered width and
    //    size the pill around it. Both shadow + main use the same
    //    string so they grow together.
    const label = `${this.currentBet}`;
    this.digitText.text = label;
    this.digitShadow.text = label;

    // 2) Measure the contents — digit width + coin width + the gap
    //    between them. Coin width is 0 when the sprite isn't loaded.
    const digitW = this.digitText.width;
    const coinW = this.coinSprite ? this.coinSprite.width : 0;
    const gap = this.coinSprite ? COIN_DIGIT_GAP : 0;
    const contentW = digitW + gap + coinW;
    // Locked-coin asymmetric shift: the coin-locked PNG carries
    // ~10% transparent margin around the visible disc, so when the
    // content block is centred the visible coin sits well inside
    // the pill's right edge — reading as a fat right gap. Producer
    // brief 2026-05-12: "move the padlock coin to the right
    // reducing the gap on the right." Tighten the pill by
    // {@code COIN_LOCKED_RIGHT_TIGHTEN} AND shift the content block
    // right by the same amount, so the visible coin's right edge
    // lands closer to the pill's right edge while the digit gets a
    // touch more breathing room on the left.
    const locked = !!(
      this.coinSprite &&
      this.coinSprite.texture ===
        AssetLoader.shared.getTexture(COIN_LOCKED_ICON_ALIAS)
    );
    const lockedTighten = locked ? COIN_LOCKED_RIGHT_TIGHTEN : 0;
    const contentShift = locked ? COIN_LOCKED_RIGHT_TIGHTEN / 2 : 0;
    const pillW = Math.max(
      PILL_MIN_WIDTH,
      Math.round(contentW + PILL_PADDING_X * 2 - lockedTighten),
    );
    this.renderedWidth = pillW;

    const halfW = pillW / 2;
    const halfH = PILL_HEIGHT / 2;
    const radius = halfH; // perfect-pill corner

    // 3) Pill background is drawn by the host (SlotHudComponent's
    //    {@code betPillPlinth}) at world scale so its gold border
    //    matches the sibling INFO + SETTINGS cluster pill pixel-for-
    //    pixel on every viewport. Producer brief 2026-05-12: "if you
    //    are [matching the cluster], then don't use a background —
    //    instead replicate the hud from a rounded rectangle with
    //    gold stroke and the same blue black gradient." Done at the
    //    SlotHudComponent layer (same drawing path as the cluster
    //    plinth).
    //
    //    Set the hit area explicitly so taps land on the full pill
    //    surface, not just the digit text bounds (the bg used to
    //    provide this implicitly).
    this.hitArea = new Rectangle(-halfW, -halfH, pillW, PILL_HEIGHT);
    void radius;

    // 4) Position digits + coin: digit text takes the left side of the
    //    content block, coin takes the right. Both vertically centred
    //    on the pill's y=0 axis (anchor 0.5, 0.5 already centres them).
    //    Locked variant shifts the content right by {@code contentShift}
    //    to compensate for the coin-locked PNG's transparent margin
    //    (see step 2 above).
    const contentLeft = -contentW / 2 + contentShift;
    const digitCenterX = contentLeft + digitW / 2;
    this.digitText.x = digitCenterX;
    this.digitText.y = 0;
    this.digitShadow.x = digitCenterX;
    // Shadow sits 2px below the main label for a "stamped" depth read.
    this.digitShadow.y = 2;

    if (this.coinSprite) {
      const coinCenterX = contentLeft + digitW + gap + coinW / 2;
      this.coinSprite.x = coinCenterX;
      this.coinSprite.y = 0;
    }
  }
}

/** Force smooth minification on a Pixi texture's underlying source.
 *  Pixi v8's default scaleMode + lack of mipmaps make heavy
 *  downscales (e.g. 1254px → 36px coin sprite) read as jagged /
 *  pixelated. Setting scaleMode to {@code linear} and
 *  {@code autoGenerateMipmaps = true} produces a clean
 *  anti-aliased read at small display sizes.
 *
 *  Pixi shares the {@code source} across every Sprite that uses the
 *  same texture, so this is a one-time per-texture operation —
 *  re-applying is cheap (idempotent assignment). */
function applySmoothMinification(texture: Texture): void {
  const source = texture.source;
  if (!source) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = source as any;
  s.scaleMode = 'linear';
  if ('autoGenerateMipmaps' in s) s.autoGenerateMipmaps = true;
  if (typeof s.update === 'function') s.update();
}
